import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';

export default function BookingConfirmationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadBooking = async () => {
      try {
        const response = await axiosInstance.get(`/bookings/${id}`);
        setBooking(response.data);
      } catch (error) {
        toast.error('Unable to load booking');
        navigate('/profile');
      } finally {
        setLoading(false);
      }
    };

    loadBooking();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-sand flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562]"></div>
      </div>
    );
  }

  if (!booking) return null;

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-28 text-[#2B3A67]" style={{ fontFamily: "'Outfit', sans-serif" }}>
      <header className="px-6 pt-6">
        <button
          data-testid="back-button"
          onClick={() => navigate('/events')}
          className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-sm border border-gray-100"
        >
          <i className="ph-bold ph-arrow-left text-xl"></i>
        </button>
      </header>

      <main className="px-6 pt-4 space-y-6">
        <div className="text-center">
          <p className="text-xs uppercase tracking-widest text-gray-400" data-testid="booking-subtitle">Your ticket is ready</p>
          <h1 data-testid="booking-title" className="text-3xl font-extrabold mt-2">Booking Confirmed</h1>
        </div>

        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden" data-testid="booking-ticket-card">
          <div className="bg-[#2B3A67] text-white p-6">
            <h2 data-testid="booking-event-title" className="text-xl font-extrabold">
              {booking.event_title}
            </h2>
            <p data-testid="booking-event-date" className="text-xs text-white/70 mt-2">
              {formatDate(booking.event_date)}
            </p>
            <p data-testid="booking-event-venue" className="text-xs text-white/70">
              {booking.venue_name}
            </p>
          </div>

          <div className="p-6 space-y-4">
            <div>
              <h3 data-testid="booking-items-title" className="text-sm font-extrabold uppercase tracking-widest text-gray-400 mb-3">
                Tickets
              </h3>
              <div className="space-y-3">
                {booking.items.map((item, index) => (
                  <div
                    key={`${item.tier_id}-${index}`}
                    data-testid={`booking-item-${index}`}
                    className="flex items-center justify-between"
                  >
                    <div>
                      <p data-testid={`booking-item-name-${index}`} className="font-bold text-sm">
                        {item.tier_name}
                      </p>
                      <p data-testid={`booking-item-quantity-${index}`} className="text-xs text-gray-500">
                        {item.quantity} ticket{item.quantity > 1 ? 's' : ''}
                      </p>
                    </div>
                    <p data-testid={`booking-item-subtotal-${index}`} className="font-bold text-sm">
                      {formatCurrency(item.subtotal)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-dashed border-gray-200 pt-4">
              <span data-testid="booking-total-label" className="font-extrabold">Total</span>
              <span data-testid="booking-total" className="font-extrabold text-lg">{formatCurrency(booking.total_amount)}</span>
            </div>
          </div>

          <div className="bg-gray-50 border-t border-dashed border-gray-200 p-6 flex flex-col items-center" data-testid="booking-qr-section">
            <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100">
              <img
                data-testid="booking-qr-code"
                src={`data:image/png;base64,${booking.qr_code}`}
                alt="Booking QR code"
                className="w-56 h-56"
                style={{ imageRendering: 'pixelated' }}
              />
            </div>
            <p data-testid="booking-qr-note" className="text-xs text-gray-500 mt-3 text-center">
              Show this QR code at the venue entrance for check-in.
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            data-testid="view-bookings-button"
            onClick={() => navigate('/profile')}
            className="flex-1 py-4 rounded-2xl bg-[#E54562] text-white font-extrabold"
          >
            View All Bookings
          </button>
          <button
            data-testid="browse-events-button"
            onClick={() => navigate('/events')}
            className="flex-1 py-4 rounded-2xl border-2 border-[#2B3A67] text-[#2B3A67] font-extrabold"
          >
            Browse More Events
          </button>
        </div>
      </main>
    </div>
  );
}